import React from "react";
import "../App.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";
import config from "../config";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/layout.css";
import "./Studio.css";
import ChannelFormModal, {
  getDefaultChannelForm,
} from "../components/ChannelFormModal";

library.add(fas);

const optionToast = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const getService = (item) =>
  String(item?.service || item?.type || "")
    .toLowerCase()
    .trim();

const isRadio = (item) => getService(item) === "radio";
const isLive = (item) => {
  const service = getService(item);
  return service === "live" || (!service && !isRadio(item));
};

class Stream extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      channels: [],
      loading: true,
      search: "",
      typeFilter: "all",
      createModal: false,
      createService: "live",
      createForm: getDefaultChannelForm(),
      createSubmitting: false,
    };
  }

  componentDidMount = () => {
    this.feedChannel();
  };

  feedChannel = () => {
    axios
      .post(config.host + "/api/v1/live/stream", {
        userid: localStorage.StudioUserID,
      })
      .then((res) => {
        this.setState({
          channels: res.data.result || [],
        });
      })
      .catch(() => {
        this.setState({ channels: [] });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handleSearchChange = (event) => {
    this.setState({ search: event.target.value });
  };

  clearSearch = () => {
    this.setState({ search: "" });
  };

  setTypeFilter = (typeFilter) => {
    this.setState({ typeFilter });
  };

  openCreateModal = (service) => {
    this.setState({
      createModal: true,
      createService: service,
      createForm: getDefaultChannelForm(),
      createSubmitting: false,
    });
  };

  closeCreateModal = () => {
    if (this.state.createSubmitting) return;
    this.setState({
      createModal: false,
      createForm: getDefaultChannelForm(),
    });
  };

  handleCreateFormChange = (createForm) => {
    this.setState({ createForm });
  };

  createChannel = () => {
    const { createForm, createService, createSubmitting } = this.state;

    if (createSubmitting) return;

    const name = createForm.name.trim();
    if (!name) {
      toast.error("Please enter a channel name", optionToast);
      return;
    }

    this.setState({ createSubmitting: true });

    axios
      .post(config.host + "/api/v1/live/stream/create", {
        userid: localStorage.StudioUserID,
        service: createService,
        name,
        desc: createForm.desc.trim(),
        enable: createForm.enable,
        transcoder: createForm.transcoder,
        rec_profile: createService === "radio" ? "1080p" : createForm.rec_profile,
        option: "none",
        hls_option: createForm.hls_option,
      })
      .then((res) => {
        if (res.data.status === "success") {
          const created = res.data.result;
          toast.success(
            createService === "radio"
              ? "Radio channel created"
              : "Live channel created",
            optionToast
          );
          this.setState((prev) => ({
            createModal: false,
            createForm: getDefaultChannelForm(),
            createSubmitting: false,
            channels: created
              ? [
                  ...prev.channels.filter((item) => item.id !== created.id),
                  created,
                ].sort(
                  (a, b) => (parseInt(a.no, 10) || 0) - (parseInt(b.no, 10) || 0)
                )
              : prev.channels,
          }));
          this.feedChannel();
          return;
        }

        toast.error(res.data.message || "Failed to create channel", optionToast);
        this.setState({ createSubmitting: false });
      })
      .catch(() => {
        toast.error("Failed to create channel", optionToast);
        this.setState({ createSubmitting: false });
      });
  };

  getSearchHaystack = (item) => {
    const radio = isRadio(item);
    const online = item.isconnect === "true";
    const recording = item.isrecording === "true";

    return [
      item.name,
      item.id,
      item._id,
      item.stream,
      item.streamkey,
      item.stream_output,
      item.stream_key,
      item.service,
      item.type,
      radio ? "radio" : "live",
      radio ? "audio" : "video",
      online ? "online" : "offline",
      recording ? "recording" : "idle",
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase())
      .join(" ");
  };

  matchesSearch = (item, query) => {
    if (!query) return true;
    const haystack = this.getSearchHaystack(item);
    return query
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .every((token) => haystack.includes(token));
  };

  getFilteredChannels = () => {
    const { channels, search, typeFilter } = this.state;
    const query = search.trim();

    return channels.filter((item) => {
      if (typeFilter === "live" && !isLive(item)) return false;
      if (typeFilter === "radio" && !isRadio(item)) return false;
      return this.matchesSearch(item, query);
    });
  };

  chooseCh(e, data) {
    this.props.history.push("/studio/" + data);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  renderChannelCard = (item, index) => {
    const radio = isRadio(item);

    return (
      <article className="stream-card" key={item.id || index}>
        <div className="stream-card__header">
          <h3 className="stream-card__title">{item.name}</h3>
          <span
            className={`stream-card__badge ${
              radio ? "stream-card__badge--radio" : "stream-card__badge--live"
            }`}
          >
            {radio ? "Radio" : "Live"}
          </span>
        </div>
        <div className="stream-card__body">
          <div className="stream-card__status-grid">
            <div className="stream-card__status">
              <span className="stream-card__status-label">Source</span>
              <span
                className={`stream-card__status-value ${
                  item.isconnect === "true" ? "is-online" : "is-offline"
                }`}
              >
                <FontAwesomeIcon
                  icon={["fas", radio ? "broadcast-tower" : "video"]}
                />
                {item.isconnect === "true" ? "Online" : "Offline"}
              </span>
            </div>
            <div className="stream-card__status">
              <span className="stream-card__status-label">Recorder</span>
              <span
                className={`stream-card__status-value ${
                  item.isrecording === "true" ? "is-recording" : "is-idle"
                }`}
              >
                {item.isrecording === "true" ? (
                  <>
                    <span className="stream-card__rec-dot" />
                    Recording
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={["fas", "circle"]} />
                    Idle
                  </>
                )}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="stream-card__studio-btn"
            onClick={(e) => this.chooseCh(e, item.id)}
          >
            Open Studio
          </button>
        </div>
      </article>
    );
  };

  renderSection = (title, subtitle, icon, items, serviceType) => {
    const createLabel =
      serviceType === "radio" ? "Create Radio" : "Create Channel";

    return (
      <section className="streams-section">
        <div className="streams-section__header">
          <div className="streams-section__title-wrap">
            <FontAwesomeIcon icon={["fas", icon]} />
            <div>
              <h2 className="streams-section__title">
                {title}
                <span className="streams-section__count">{items.length}</span>
              </h2>
              <p className="streams-section__subtitle">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="streams-section__add-btn"
            onClick={() => this.openCreateModal(serviceType)}
            aria-label={createLabel}
          >
            <FontAwesomeIcon icon={["fas", "plus"]} />
            {createLabel}
          </button>
        </div>
        {items.length > 0 ? (
          <div className="streams-grid">
            {items.map((item, index) => this.renderChannelCard(item, index))}
          </div>
        ) : (
          <div className="streams-section__empty">
            <p>No {serviceType === "radio" ? "radio" : "live"} channels yet</p>
            <button
              type="button"
              className="streams-section__empty-btn"
              onClick={() => this.openCreateModal(serviceType)}
            >
              <FontAwesomeIcon icon={["fas", "plus"]} />
              {createLabel}
            </button>
          </div>
        )}
      </section>
    );
  };

  shouldShowLiveSection = () => {
    const { typeFilter } = this.state;
    return typeFilter === "all" || typeFilter === "live";
  };

  shouldShowRadioSection = () => {
    const { typeFilter } = this.state;
    return typeFilter === "all" || typeFilter === "radio";
  };

  renderCreateModal = () => {
    const { createModal, createForm, createSubmitting, createService } =
      this.state;

    return (
      <ChannelFormModal
        isOpen={createModal}
        onClose={this.closeCreateModal}
        onSubmit={this.createChannel}
        values={createForm}
        onChange={this.handleCreateFormChange}
        submitting={createSubmitting}
        mode="create"
        serviceType={createService}
      />
    );
  };

  render() {
    const { channels, loading, search, typeFilter } = this.state;
    const filtered = this.getFilteredChannels();
    const liveChannels = filtered.filter(isLive);
    const radioChannels = filtered.filter(isRadio);
    const hasChannels = channels.length > 0;
    const hasActiveFilter = Boolean(search.trim()) || typeFilter !== "all";
    const showLiveSection = this.shouldShowLiveSection();
    const showRadioSection = this.shouldShowRadioSection();
    const showNoSearchResults =
      hasChannels &&
      hasActiveFilter &&
      !liveChannels.length &&
      !radioChannels.length;

    return (
      <div className="app-shell">
        <Navbar />
        <ToastContainer />
        <main className="app-main">
          <div className="streams-page">
            <div className="streams-page__header">
              <div className="streams-page__heading">
                <h1 className="streams-page__title">Live Streams</h1>
                <p className="streams-page__subtitle">
                  Select a live stream or radio channel to open the studio
                </p>
              </div>

              <div className="streams-toolbar">
                <div className="streams-search">
                  <FontAwesomeIcon
                    icon={["fas", "search"]}
                    className="streams-search__icon"
                  />
                  <input
                    type="search"
                    className="streams-search__input"
                    placeholder="Search name, stream key, live, radio, online…"
                    value={search}
                    onChange={this.handleSearchChange}
                    aria-label="Search channels"
                  />
                  {search ? (
                    <button
                      type="button"
                      className="streams-search__clear"
                      onClick={this.clearSearch}
                      aria-label="Clear search"
                    >
                      <FontAwesomeIcon icon={["fas", "times"]} />
                    </button>
                  ) : null}
                </div>

                <div className="streams-type-filters" role="group" aria-label="Channel type">
                  {[
                    { id: "all", label: "All" },
                    { id: "live", label: "Live" },
                    { id: "radio", label: "Radio" },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`streams-type-filters__btn${
                        typeFilter === option.id
                          ? " streams-type-filters__btn--active"
                          : ""
                      }`}
                      onClick={() => this.setTypeFilter(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="streams-loading" aria-live="polite" aria-busy="true">
                <FontAwesomeIcon
                  icon={["fas", "spinner"]}
                  spin
                  className="streams-loading__icon"
                />
                <p>Loading channels...</p>
              </div>
            ) : showNoSearchResults ? (
              <div className="streams-empty">
                <FontAwesomeIcon
                  icon={["fas", "search"]}
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.75rem",
                    opacity: 0.4,
                  }}
                />
                <p style={{ margin: 0 }}>No channels match your search</p>
                <button
                  type="button"
                  className="streams-empty__reset"
                  onClick={() =>
                    this.setState({ search: "", typeFilter: "all" })
                  }
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                {showLiveSection
                  ? this.renderSection(
                      "Live Stream",
                      "Video live channels",
                      "video",
                      liveChannels,
                      "live"
                    )
                  : null}
                {showRadioSection
                  ? this.renderSection(
                      "Radio",
                      "Audio radio channels",
                      "broadcast-tower",
                      radioChannels,
                      "radio"
                    )
                  : null}
              </>
            )}
          </div>
        </main>
        {this.renderCreateModal()}
        <Footer />
      </div>
    );
  }
}

export default Stream;
